'use client'

import type { InboxCounts, InboxNotification } from '@ordo/notifications-client/contract'
import { Bell, BellOff, BellPlus, BellRing, CheckCheck, CircleCheck, Settings, X } from 'lucide-react'
import * as React from 'react'
import { createPortal } from 'react-dom'

import { cn } from '../cn'
import { useIsMobile } from '../hooks/use-is-mobile'
import { useLoadMore, type UseLoadMoreResult } from '../hooks/use-load-more'
import {
  buildNotificationSections,
  bundleLineText,
  bundleSentence,
  defaultActionOutcome,
  entryIsUnread,
  entryNotifications,
  filterNotificationsByTab,
  findNotificationEntry,
  groupDayKey,
  groupMetaLine,
  groupUnreadCount,
  type InboxEntry,
  type InboxGroupEntry,
  type InboxItemEntry,
  type InboxSection,
  isNotificationRead,
  itemKindLabel,
  NOTIFICATION_ACTION_LABELS,
  NOTIFICATION_TABS,
  notificationEntryOrder,
  notificationItemKey,
  type NotificationPushControl,
  type NotificationQuickAction,
  type NotificationTab,
  notificationTimeLabel,
  openLabel,
  resolvedLine,
  uniqueSenders,
} from '../notification-inbox'
import { useShellLayout } from './app-shell'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './dropdown-menu'
import { IOS_ADD_TO_HOME_SCREEN_STEPS } from './install-prompt'
import {
  NotificationDetail,
  NotificationDetailEmpty,
  type NotificationDetailState,
  NotificationGroupDetail,
  type NotificationQuickAnswer,
  quickAnswerNote,
} from './notification-detail'
import { NotificationGenericCircle, NotificationPicture, NotificationSentence } from './notification-picture'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Skeleton } from './skeleton'

export type { NotificationTab } from '../notification-inbox'

export interface NotificationBellUnavailableProps {
  /** Why there are no notifications; read by screen readers and shown on hover. */
  label?: string
  /** Same slot as `NotificationCenter`'s, e.g. `SHELL_CIRCLE_CLASS` on phones. */
  triggerClassName?: string
}

/**
 * A greyed-out bell for apps (or users) with no notifications, so the bell
 * sits in the same place in every app instead of leaving a gap. It opens
 * nothing. `aria-disabled` rather than `disabled` so the hover label still
 * shows on desktop.
 */
export function NotificationBellUnavailable({
  label = "Notifications aren't available in this app yet",
  triggerClassName,
}: NotificationBellUnavailableProps) {
  return (
    <button
      type="button"
      aria-disabled="true"
      aria-label={label}
      title={label}
      className={cn(
        'relative inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground focus:outline-none',
        triggerClassName,
        'cursor-not-allowed opacity-40 active:scale-100',
      )}>
      <Bell className="h-5 w-5" />
    </button>
  )
}

/** Shared by the bell and the page. Data comes from the app (today its own feed; later `useNotifications()`). */
export interface NotificationInboxProps {
  items: InboxNotification[]
  /** Unread counts. The bell shows `total`; the Assigned, Mentioned and Following pills show their own. */
  counts: InboxCounts
  loading?: boolean
  /** The app could not reach its notifications. */
  unavailable?: boolean
  unavailableMessage?: string
  /** Controlled tab. Leave unset to let the component keep it; rows are filtered by bucket either way. */
  tab?: NotificationTab
  onTabChange?: (tab: NotificationTab) => void
  title?: string
  /** Extra controls beside "Mark all read". For the settings gear use `settingsHref`. */
  headerActions?: React.ReactNode
  /**
   * Where the header's settings gear goes (the person's notification
   * settings). Drawn as the same icon button as the push control on every
   * surface, so the two always match in size. Hidden when unset.
   */
  settingsHref?: string
  /** "Enable push on this device", from `pushControlFrom(usePushSubscription())`. Hidden when unset. */
  pushControl?: NotificationPushControl
  emptyDescription?: string
  /** Where clicking goes. Defaults to the item's absolute url in the same tab. Opening marks the row read first. */
  onOpen?: (notification: InboxNotification, target: 'primary' | 'secondary') => void | Promise<void>
  onMarkRead?: (notification: InboxNotification) => void | Promise<void>
  onMarkUnread?: (notification: InboxNotification) => void | Promise<void>
  /** Clears only the given tab; All clears everything. */
  onMarkAllRead?: (tab: NotificationTab) => void | Promise<void>
  /** Shows "Unfollow {kind}" on Following rows. */
  onUnfollow?: (notification: InboxNotification) => void | Promise<void>
  /** Shows "Undo" after an unfollow. */
  onFollowAgain?: (notification: InboxNotification) => void | Promise<void>
  /** One-click Approve / Decline. Resolve with the line to show ("You approved the $48,000 bid bond."); throw to show the app's refusal. */
  onAction?: (notification: InboxNotification, action: NotificationQuickAction) => Promise<string | void>
  /** Shows the reply box on conversations. */
  onReply?: (notification: InboxNotification, text: string) => Promise<void>
  hasMore?: boolean
  onLoadMore?: () => void | Promise<void>
  /** Fixed clock for stories and tests. */
  now?: Date
}

export interface NotificationCenterProps extends NotificationInboxProps {
  /** "See all notifications" at the bottom of the popover: the app's own /notifications page. */
  onSeeAll?: () => void
  triggerClassName?: string
  className?: string
}

export interface NotificationCenterPageProps extends NotificationInboxProps {
  /**
   * Fetches the right side from the owning app. Resolve `null` for the Generic
   * framework; throw (or resolve `unavailable`) when the app is down. Without
   * it every notification uses Generic.
   */
  loadDetail?: (
    notification: InboxNotification,
  ) => Promise<Exclude<NotificationDetailState, { status: 'loading' }> | null>
  /** The row selected on first render (a deep link, a story, a test). Desktop only. */
  defaultSelectedId?: string
  className?: string
}

const MARK_ALL_LABEL = 'Mark all read'
const DEFAULT_EMPTY = 'New notifications from every app show up here.'
const UNAVAILABLE = 'Notifications are unavailable right now. They will show up again shortly.'

export function notificationCenterUnreadLabel(unreadCount: number): string | null {
  if (unreadCount <= 0) {
    return null
  }
  return unreadCount > 99 ? '99+' : String(unreadCount)
}

/** Unread count for one tab; All is the bell's total. */
export function notificationTabUnread(counts: InboxCounts, tab: NotificationTab): number {
  return tab === 'all' ? counts.total : counts[tab]
}

/**
 * The counts after this session's own read and unread clicks, so the bell and
 * the pills move at once instead of after the next refetch.
 */
export function adjustNotificationCounts(
  counts: InboxCounts,
  items: readonly InboxNotification[],
  overrides: Readonly<Record<string, boolean>>,
): InboxCounts {
  const next = { ...counts }
  for (const item of items) {
    const read = overrides[item.id]
    if (read === undefined || read === (item.readAt !== null)) {
      continue
    }
    const delta = read ? -1 : 1
    next[item.bucket] = Math.max(0, next[item.bucket] + delta)
    next.total = Math.max(0, next.total + delta)
  }
  return next
}

// The unified shell's phone panel grows out of the bell, pinned to the bell's
// top-right corner, and shrinks back into it. No overshoot: a bouncy spring on
// the edges reads as a slide rather than a grow.
const MORPH_MS = 460
const MORPH_EASE = 'cubic-bezier(.2,.9,.25,1)'

export interface CornerGrowOrigin {
  top: number
  right: number
  width: number
  height: number
  radius: number
}

/**
 * Where the phone panel starts and ends: the trigger's own box, measured from
 * the viewport's top-right so the panel stays pinned to that corner while it
 * grows to full screen. Exported for tests.
 */
export function cornerGrowOrigin(
  trigger: Pick<DOMRect, 'top' | 'right' | 'width' | 'height'>,
  viewportWidth: number,
): CornerGrowOrigin {
  return {
    top: trigger.top,
    right: viewportWidth - trigger.right,
    width: trigger.width,
    height: trigger.height,
    radius: Math.min(trigger.width, trigger.height) / 2,
  }
}

// ── Shared state ─────────────────────────────────────────────────────────────

const FADE_AFTER_MS = 2600
const DONE_AFTER_MS = 3300

/** Read state, quick answers, unfollows and the tab: everything the bell and the page share. */
function useInbox(props: NotificationInboxProps) {
  const { items, onMarkRead, onMarkUnread, onMarkAllRead, onAction, onUnfollow, onFollowAgain, onOpen } = props
  const [ownTab, setOwnTab] = React.useState<NotificationTab>('all')
  const tab = props.tab ?? ownTab
  const [overrides, setOverrides] = React.useState<Record<string, boolean>>({})
  const [answers, setAnswers] = React.useState<Record<string, NotificationQuickAnswer>>({})
  const [unfollowed, setUnfollowed] = React.useState<ReadonlySet<string>>(() => new Set())
  // A group line was clicked: the Following tab opens at the section for this
  // day (a `notificationDayKey`) once it is drawn. State here rather than in the
  // app so it works whether or not the app controls `tab`.
  const [pendingFollowingDay, setPendingFollowingDay] = React.useState<string | null>(null)
  const [highlightedDay, setHighlightedDay] = React.useState<string | null>(null)
  const timers = React.useRef<number[]>([])
  const now = React.useMemo(() => props.now ?? new Date(), [props.now, items])

  // A refetch carries the server's truth, so local read flips are dropped.
  React.useEffect(() => setOverrides({}), [items])
  React.useEffect(() => () => timers.current.forEach((timer) => window.clearTimeout(timer)), [])

  const isRead = React.useCallback(
    (notification: InboxNotification) => overrides[notification.id] ?? isNotificationRead(notification),
    [overrides],
  )
  const counts = React.useMemo(
    () => adjustNotificationCounts(props.counts, items, overrides),
    [props.counts, items, overrides],
  )
  const sections = React.useMemo(() => buildNotificationSections(items, tab, now), [items, tab, now])

  const setTab = (next: NotificationTab) => {
    if (props.tab === undefined) {
      setOwnTab(next)
    }
    props.onTabChange?.(next)
    // Picking a tab by hand cancels a jump still waiting for its day.
    setPendingFollowingDay(null)
  }

  const clearPendingFollowingDay = React.useCallback(() => setPendingFollowingDay(null), [])

  /** Switches to Following and lands on that day's section (see `useFollowingDayJump`). */
  const openFollowingAt = (day: string) => {
    setTab('following')
    setPendingFollowingDay(day)
  }

  const setRead = (notifications: readonly InboxNotification[], read: boolean, notify = true) => {
    const changed = notifications.filter((notification) => isRead(notification) !== read)
    if (changed.length === 0) {
      return
    }
    setOverrides((current) => ({ ...current, ...Object.fromEntries(changed.map((n) => [n.id, read])) }))
    if (!notify) {
      return
    }
    for (const notification of changed) {
      void (read ? onMarkRead?.(notification) : onMarkUnread?.(notification))
    }
  }

  const markAllRead = () => {
    setRead(filterNotificationsByTab(items, tab), true, false)
    void onMarkAllRead?.(tab)
  }

  const open = (notification: InboxNotification, target: 'primary' | 'secondary' = 'primary') => {
    setRead([notification], true)
    if (onOpen) {
      void onOpen(notification, target)
      return
    }
    const url =
      target === 'secondary' && notification.item.secondary ? notification.item.secondary.url : notification.item.url
    window.location.assign(url)
  }

  const setAnswer = (
    id: string,
    answer: NotificationQuickAnswer | ((current: NotificationQuickAnswer) => NotificationQuickAnswer),
  ) =>
    setAnswers((current) => {
      const previous = current[id]
      const next = typeof answer === 'function' ? (previous ? answer(previous) : undefined) : answer
      return next ? { ...current, [id]: next } : current
    })

  const answer = async (notification: InboxNotification, action: NotificationQuickAction) => {
    setRead([notification], true)
    try {
      const text = await onAction?.(notification, action)
      setAnswer(notification.id, { action, text: text || defaultActionOutcome(action), phase: 'shown' })
      timers.current.push(
        window.setTimeout(() => setAnswer(notification.id, (a) => ({ ...a, phase: 'fading' })), FADE_AFTER_MS),
        window.setTimeout(() => setAnswer(notification.id, (a) => ({ ...a, phase: 'done' })), DONE_AFTER_MS),
      )
    } catch (error) {
      setAnswer(notification.id, {
        action,
        text:
          error instanceof Error && error.message ? error.message : 'That didn’t go through. Open it to answer there.',
        phase: 'shown',
        failed: true,
      })
    }
  }

  const unfollow = (notification: InboxNotification) => {
    setUnfollowed((current) => new Set(current).add(notificationItemKey(notification)))
    void onUnfollow?.(notification)
  }
  const followAgain = (notification: InboxNotification) => {
    setUnfollowed((current) => {
      const next = new Set(current)
      next.delete(notificationItemKey(notification))
      return next
    })
    void onFollowAgain?.(notification)
  }
  const isUnfollowed = (notification: InboxNotification) => unfollowed.has(notificationItemKey(notification))

  return {
    tab,
    setTab,
    now,
    counts,
    sections,
    isRead,
    setRead,
    markAllRead,
    open,
    answers,
    answer: onAction ? answer : undefined,
    unfollow: onUnfollow ? unfollow : undefined,
    followAgain: onFollowAgain ? followAgain : undefined,
    isUnfollowed,
    tabUnread: notificationTabUnread(counts, tab),
    pendingFollowingDay,
    openFollowingAt,
    clearPendingFollowingDay,
    highlightedDay,
    setHighlightedDay,
  }
}

type Inbox = ReturnType<typeof useInbox>

const DAY_HIGHLIGHT_MS = 1600
/** Loads in a row that leave the list unchanged before a jump gives up (a failing page). */
const DAY_JUMP_MAX_MISSES = 2

/**
 * After a group line opens Following at its day: once the tab is drawn, scroll
 * the list to that day's section, focus its header and tint it briefly. When
 * the day is not loaded yet (an app that pages each tab on its own), load older
 * pages until it appears, an older day shows up, or there is nothing more;
 * then land on the nearest older day, else the top.
 */
function useFollowingDayJump({
  inbox,
  rootRef,
  loader,
  canLoadMore,
}: {
  inbox: Inbox
  rootRef: React.RefObject<HTMLElement | null>
  loader: UseLoadMoreResult
  canLoadMore: boolean
}) {
  const { pendingFollowingDay: day, tab, sections, clearPendingFollowingDay, setHighlightedDay } = inbox
  const { loadingMore, loadMore } = loader
  const attempt = React.useRef<{ sections: InboxSection[]; misses: number } | null>(null)
  const highlightTimer = React.useRef<number | undefined>(undefined)

  React.useEffect(() => () => window.clearTimeout(highlightTimer.current), [])

  React.useEffect(() => {
    if (!day) {
      attempt.current = null
      return
    }
    if (tab !== 'following' || loadingMore) {
      return
    }
    const found = sections.some((section) => section.key === day)
    // Sections run newest first and day keys sort as dates.
    const olderLoaded = sections.some((section) => section.key < day)
    if (!found && !olderLoaded && canLoadMore) {
      const last = attempt.current
      const misses = last && last.sections === sections ? last.misses + 1 : 0
      if (misses < DAY_JUMP_MAX_MISSES) {
        attempt.current = { sections, misses }
        void loadMore()
        return
      }
    }
    attempt.current = null
    clearPendingFollowingDay()
    const target = found ? day : (sections.find((section) => section.key < day)?.key ?? null)
    const root = rootRef.current
    const heading = target ? (root ?? document).querySelector<HTMLElement>(`[data-notification-day="${target}"]`) : null
    if (!heading) {
      root?.scrollTo?.({ top: 0 })
      return
    }
    heading.scrollIntoView?.({ block: 'start' })
    heading.focus?.({ preventScroll: true })
    setHighlightedDay(target)
    window.clearTimeout(highlightTimer.current)
    highlightTimer.current = window.setTimeout(() => setHighlightedDay(null), DAY_HIGHLIGHT_MS)
  }, [day, tab, sections, loadingMore, canLoadMore, loadMore, rootRef, clearPendingFollowingDay, setHighlightedDay])
}

// ── Rows ─────────────────────────────────────────────────────────────────────

type Density = 'popover' | 'page' | 'phone'

const DENSITY: Record<
  Density,
  {
    row: string
    text: string
    quote: string
    meta: string
    actions: string
    actionButton: string
    more: string
    subs: string
    sub: string
    section: string
  }
> = {
  popover: {
    row: 'py-3 pl-4',
    text: 'text-sm leading-5',
    quote: 'truncate text-sm leading-5',
    meta: 'text-xs leading-4',
    actions: 'pb-3.5 pl-[68px] pr-4',
    actionButton: 'h-9 flex-1 text-sm',
    more: 'ml-1 mr-2 mt-2.5 h-[30px] w-[34px] text-base',
    subs: 'pb-2.5 pl-[68px] pr-4',
    sub: 'h-[34px] text-[13px]',
    section: 'px-4 pb-1.5 pt-3.5 text-xs',
  },
  page: {
    row: 'py-3.5 pl-5',
    text: 'text-sm leading-5',
    quote: 'truncate text-sm leading-5',
    meta: 'text-xs leading-4',
    actions: 'pb-3.5 pl-[72px] pr-5',
    actionButton: 'h-[34px] max-w-[150px] flex-1 text-[13px]',
    more: 'ml-1 mr-2.5 mt-3 h-[30px] w-[34px] text-base',
    subs: 'pb-2.5 pl-[72px] pr-5',
    sub: 'h-[34px] text-[13px]',
    section: 'border-b border-border px-5 pb-2 pt-4 text-[13px]',
  },
  phone: {
    row: 'py-3.5 pl-[18px]',
    text: 'text-[15px] leading-[21px]',
    quote: 'line-clamp-2 text-[15px] leading-[21px]',
    meta: 'text-[13px] leading-[18px]',
    actions: 'pb-4 pl-[76px] pr-4',
    actionButton: 'h-[42px] flex-1 text-[15px]',
    more: 'ml-1 mr-2 mt-3 h-9 w-10 text-lg',
    subs: 'pb-2.5 pl-[76px] pr-4',
    sub: 'h-[42px] text-sm',
    section: 'px-[18px] pb-1.5 pt-3.5 text-[13px]',
  },
}

export type NotificationRowState = 'plain' | 'unread' | 'selected'

/** Selected (the row whose detail is open on the right) wins over unread. Exported for tests. */
export function notificationRowState(selected: boolean, unread: boolean): NotificationRowState {
  return selected ? 'selected' : unread ? 'unread' : 'plain'
}

// The row's own colour, kept opaque so the picture's cut-out ring
// (NOTIFICATION_ROW_COLOR) matches it: the list surface mixed with the primary
// token by `--notification-tint`. It reads Tailwind's `--color-*` theme tokens,
// never the raw `--primary` / `--background`: most apps define those as bare HSL
// triplets (`230 78% 56%`), which are not colours, so a mix or a shadow built on
// them is dropped and every row looks the same.
const ROW_COLOR =
  'bg-[var(--notification-row)] [--notification-row:color-mix(in_srgb,var(--color-primary)_var(--notification-tint,0%),var(--notification-surface,var(--color-background)))]'

// V9: unread rows sit on a pale primary tint; the selected row on a stronger one
// with a 2px primary outline. Dark mode tints a little more so it still reads on
// the dark surface.
const ROW_STATE_CLASS: Record<NotificationRowState, string> = {
  plain: '',
  unread: '[--notification-tint:8%] dark:[--notification-tint:12%]',
  selected: '[--notification-tint:16%] dark:[--notification-tint:22%] shadow-[inset_0_0_0_2px_var(--color-primary)]',
}

/** The row's state classes plus `data-selected` / `data-unread`. Exported for tests. */
export function notificationRowProps(
  selected: boolean,
  unread: boolean,
): { className: string; 'data-selected': boolean; 'data-unread': boolean } {
  return {
    className: cn(ROW_COLOR, ROW_STATE_CLASS[notificationRowState(selected, unread)]),
    'data-selected': selected,
    'data-unread': unread,
  }
}

interface ListHandlers {
  density: Density
  inbox: Inbox
  selectedId?: string | null
  /** Page: a click selects (the right side shows it). Bell and phone: a click opens. */
  onSelect?: (entry: InboxEntry) => void
  expanded?: ReadonlySet<string>
  onToggleExpanded?: (id: string) => void
  /** Opens the Following tab at the group's day; `entry` picks a row there (the page). */
  onOpenFollowing: (group: InboxGroupEntry, entry?: InboxItemEntry) => void
}

function RowMenu({
  label,
  className,
  items,
}: {
  label: string
  className: string
  items: ({ label: string; onSelect: () => void } | null)[]
}) {
  const shown = items.filter((item): item is { label: string; onSelect: () => void } => item !== null)
  if (shown.length === 0) {
    return null
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn(
            'shrink-0 rounded-lg leading-none text-muted-foreground transition-[opacity,background-color] hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-muted data-[state=open]:opacity-100',
            className,
          )}>
          ···
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[190px]">
        {shown.map((item) => (
          <DropdownMenuItem key={item.label} onClick={item.onSelect}>
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function moreVisibility(density: Density, selected: boolean): string {
  // On the page "···" shows on hover or when the row is selected; the bell and phone always show it.
  return density === 'page' && !selected ? 'opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100' : ''
}

function ItemRow({ entry, handlers }: { entry: InboxItemEntry; handlers: ListHandlers }) {
  const { density, inbox, selectedId, onSelect, expanded, onToggleExpanded } = handlers
  const d = DENSITY[density]
  const notifications = entry.kind === 'bundle' ? entry.notifications : [entry.notification]
  const lead = notifications[0] as InboxNotification
  const isBundle = entry.kind === 'bundle'
  const unread = entryIsUnread(entry, inbox.isRead)
  const selected = selectedId === entry.id
  const isExpanded = isBundle && expanded?.has(entry.id) === true
  const kind = itemKindLabel(lead.item.kind)
  const answer = inbox.answers[lead.id]

  if (inbox.isUnfollowed(lead)) {
    const gone = notificationRowProps(selected, false)
    return (
      <div
        {...gone}
        className={cn(
          'flex items-center gap-2 px-4 py-3',
          gone.className,
          density === 'page' && 'border-b border-dashed border-border px-5',
        )}>
        <span className={cn('min-w-0 flex-1 text-muted-foreground', d.text)}>
          You unfollowed <span className="font-semibold text-foreground">{lead.item.label}</span>.
        </span>
        {inbox.followAgain ? (
          <button
            type="button"
            onClick={() => inbox.followAgain?.(lead)}
            className="h-[30px] shrink-0 rounded-lg px-2.5 text-[13px] font-semibold text-link hover:text-link-hover">
            Undo
          </button>
        ) : null}
      </div>
    )
  }

  const activate = () => {
    if (onSelect) {
      inbox.setRead(notifications, true)
      onSelect(entry)
      return
    }
    if (isBundle) {
      inbox.setRead(notifications, true)
      onToggleExpanded?.(entry.id)
      return
    }
    inbox.open(lead)
  }
  const meta = `${notificationTimeLabel(lead.createdAt, inbox.now)}${
    isBundle ? ` · ${notifications.length} updates${onSelect ? '' : isExpanded ? ' ▴' : ' ▾'}` : ''
  }${quickAnswerNote(answer)}`
  const resolved = resolvedLine(lead, inbox.now)
  const row = notificationRowProps(selected, unread)
  const showButtons = !isBundle && !resolved && !answer && lead.actions.length > 0 && inbox.answer
  const outcome = !isBundle ? (resolved ?? (answer && answer.phase !== 'done' ? answer.text : null)) : null

  return (
    <div {...row} className={cn('group/row', row.className, density === 'page' && 'border-b border-border')}>
      <div className="relative flex items-start">
        <button
          type="button"
          onClick={activate}
          aria-current={onSelect ? selected : undefined}
          aria-expanded={isBundle && !onSelect ? isExpanded : undefined}
          data-notification-entry={entry.id}
          className={cn('flex min-w-0 flex-1 items-start gap-3 text-left focus:outline-none', d.row)}>
          <NotificationPicture
            senders={isBundle ? uniqueSenders(notifications) : [lead.sender]}
            app={lead.app}
            size={density === 'phone' ? 44 : 40}
          />
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <NotificationSentence
              className={cn('line-clamp-2', d.text)}
              segments={isBundle ? bundleSentence(notifications) : lead.sentence}
            />
            {!isBundle && lead.preview ? (
              <span className={cn('text-muted-foreground', d.quote)}>“{lead.preview}”</span>
            ) : null}
            <span className={cn('text-muted-foreground', d.meta)}>{meta}</span>
          </span>
        </button>
        <RowMenu
          label="More actions"
          className={cn(d.more, moreVisibility(density, selected))}
          items={[
            { label: openLabel(lead), onSelect: () => inbox.open(lead) },
            unread
              ? { label: 'Mark as read', onSelect: () => inbox.setRead(notifications, true) }
              : { label: 'Mark as unread', onSelect: () => inbox.setRead(notifications, false) },
            lead.bucket === 'following' && inbox.unfollow
              ? { label: `Unfollow ${kind}`, onSelect: () => inbox.unfollow?.(lead) }
              : null,
          ]}
        />
      </div>
      {showButtons ? (
        <div className={cn('flex gap-2', d.actions)}>
          {lead.actions.map((action) => (
            <button
              key={action}
              type="button"
              onClick={() => {
                if (onSelect) {
                  onSelect(entry)
                }
                void inbox.answer?.(lead, action)
              }}
              className={cn(
                'rounded-lg font-semibold transition-colors',
                d.actionButton,
                action === 'approve'
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
              )}>
              {NOTIFICATION_ACTION_LABELS[action].label}
            </button>
          ))}
        </div>
      ) : null}
      {outcome ? (
        <div
          role={answer?.failed ? 'alert' : 'status'}
          className={cn(
            'flex items-center gap-2 text-[13px] transition-opacity duration-500',
            d.actions,
            answer?.failed ? 'text-destructive' : 'text-muted-foreground',
            !resolved && answer?.phase === 'fading' && 'opacity-0',
          )}>
          {answer?.failed && !resolved ? null : (
            <CheckCheck className="h-[15px] w-[15px] shrink-0 text-success-foreground" strokeWidth={2.4} />
          )}
          {outcome}
        </div>
      ) : null}
      {isExpanded ? (
        <div className={cn('flex flex-col', d.subs)}>
          {notifications.map((notification) => (
            <button
              key={notification.id}
              type="button"
              onClick={() => inbox.open(notification)}
              className={cn('flex items-center gap-2.5 text-left', d.sub)}>
              <NotificationPicture senders={[notification.sender]} size={22} />
              <span className="min-w-0 flex-1 truncate text-foreground/75">
                {notification.sender.kind === 'none' ? null : (
                  <span className="mr-1 font-semibold text-foreground">{notification.sender.name}</span>
                )}
                {bundleLineText(notification)}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {notificationTimeLabel(notification.createdAt, inbox.now)}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** In All: a day's Following rows, folded into one line. The generic circle, the apps inside, never people. */
function GroupLine({ group, handlers }: { group: InboxGroupEntry; handlers: ListHandlers }) {
  const { density, inbox, selectedId, onSelect, onOpenFollowing } = handlers
  const d = DENSITY[density]
  const notifications = entryNotifications(group)
  const unread = groupUnreadCount(group, inbox.isRead)
  const meta = groupMetaLine(group)
  const selected = selectedId === group.id
  const row = notificationRowProps(selected, unread > 0)
  return (
    <div
      {...row}
      className={cn(
        'group/row relative flex items-start',
        row.className,
        density === 'page' && 'border-b border-border',
      )}>
      <button
        type="button"
        aria-label={
          onSelect
            ? undefined
            : `Updates on things you follow, ${meta}${unread > 0 ? `, ${unread} unread` : ''}. Opens the Following tab at ${group.dayLabel}.`
        }
        aria-current={onSelect ? selected : undefined}
        data-notification-entry={group.id}
        onClick={() => (onSelect ? onSelect(group) : onOpenFollowing(group))}
        className={cn('flex min-w-0 flex-1 items-start gap-3 text-left focus:outline-none', d.row)}>
        <NotificationGenericCircle size={density === 'phone' ? 44 : 40} />
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className={cn('text-foreground/80', d.text)}>
            <span className="font-semibold text-foreground">Updates</span> on things you follow
            {unread > 0 ? (
              <span
                aria-label={`${unread} unread`}
                className="ml-2 inline-block h-5 min-w-5 rounded-full bg-primary px-1.5 text-center align-[1px] text-[11px] font-bold leading-5 text-primary-foreground">
                {unread}
              </span>
            ) : null}
          </span>
          <span data-notification-group-meta="" className={cn('truncate text-muted-foreground', d.meta)}>
            {meta}
          </span>
        </span>
      </button>
      <RowMenu
        label="More actions"
        className={cn(d.more, moreVisibility(density, selected))}
        items={[
          { label: 'Open Following', onSelect: () => onOpenFollowing(group) },
          unread > 0
            ? { label: 'Mark as read', onSelect: () => inbox.setRead(notifications, true) }
            : { label: 'Mark as unread', onSelect: () => inbox.setRead(notifications, false) },
        ]}
      />
    </div>
  )
}

function NotificationSections({ sections, handlers }: { sections: InboxSection[]; handlers: ListHandlers }) {
  const d = DENSITY[handlers.density]
  return (
    <>
      {sections.map((section) => {
        const highlighted = handlers.inbox.highlightedDay === section.key
        return (
          <section key={section.key} aria-label={section.label}>
            {/* A group line's jump lands here: focused (tabIndex -1) and tinted for a moment. */}
            <h2
              tabIndex={-1}
              data-notification-day={section.key}
              data-highlighted={highlighted || undefined}
              className={cn(
                'm-0 font-semibold text-muted-foreground transition-colors duration-500 focus:outline-none',
                d.section,
                highlighted && 'bg-primary/10 text-foreground',
              )}>
              {section.label}
            </h2>
            {section.entries.map((entry) => (
              <React.Fragment key={entry.id}>
                {entry.kind === 'group' ? (
                  <GroupLine group={entry} handlers={handlers} />
                ) : (
                  <ItemRow entry={entry} handlers={handlers} />
                )}
                {handlers.density === 'page' ? null : <div className="h-px bg-border/60" />}
              </React.Fragment>
            ))}
          </section>
        )
      })}
    </>
  )
}

function TabPills({ inbox, size, className }: { inbox: Inbox; size: 'sm' | 'md' | 'lg'; className?: string }) {
  return (
    <div role="group" aria-label="Filter" className={cn('flex items-center gap-2', className)}>
      {NOTIFICATION_TABS.map((tab) => {
        const on = inbox.tab === tab.id
        const count = tab.id === 'all' ? 0 : inbox.counts[tab.id]
        return (
          <button
            key={tab.id}
            type="button"
            aria-pressed={on}
            onClick={() => inbox.setTab(tab.id)}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 font-medium transition-colors',
              size === 'sm' ? 'h-8 text-[13px]' : size === 'md' ? 'h-[34px] text-sm' : 'h-9 text-[15px]',
              on
                ? 'border-foreground bg-foreground text-background'
                : 'border-border text-foreground/85 hover:bg-muted',
            )}>
            {tab.label}
            {count > 0 ? (
              <span className="h-[18px] min-w-[18px] rounded-full bg-primary px-[5px] text-center text-[11px] font-bold leading-[18px] text-primary-foreground">
                {count > 99 ? '99+' : count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

const PUSH_COPY: Record<
  NotificationPushControl['status'],
  { label: string; title: string; description: string; action?: 'enable' | 'disable' }
> = {
  off: {
    label: 'Enable push on this device',
    title: 'Push notifications',
    description: 'Get a notification on this device when you’re assigned or mentioned, even with the app closed.',
    action: 'enable',
  },
  on: {
    label: 'Push is on for this device',
    title: 'Push is on',
    description: 'This device gets a notification when you’re assigned or mentioned.',
    action: 'disable',
  },
  blocked: {
    label: 'Push is blocked on this device',
    title: 'Notifications are blocked',
    description: 'Allow notifications for this site in your browser settings, then reopen the bell.',
  },
  'needs-home-screen': {
    label: 'Enable push on this device',
    title: 'Add to your Home Screen first',
    description: `On iPhone and iPad (iOS 16.4 or later), push works from the Home Screen app. ${IOS_ADD_TO_HOME_SCREEN_STEPS} Then open it from there and turn push on.`,
  },
}

/** "Enable push on this device": a bell-shaped button beside Mark all read, opening a short explainer and the switch. */
/** The header's icon buttons (settings gear, push). One style so they always match. */
const HEADER_ICON_BUTTON =
  'inline-flex shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'

/** A full navigation, like "See all", so an open popover does not linger over the settings page. */
function SettingsLink({ href, className }: { href?: string; className?: string }) {
  if (!href) {
    return null
  }
  return (
    <a
      href={href}
      aria-label="Notification settings"
      title="Notification settings"
      className={cn(HEADER_ICON_BUTTON, className)}>
      <Settings className="h-[17px] w-[17px]" />
    </a>
  )
}

function PushControlMenu({ control, className }: { control?: NotificationPushControl; className?: string }) {
  if (!control) {
    return null
  }
  const copy = PUSH_COPY[control.status]
  const Icon = control.status === 'on' ? BellRing : control.status === 'blocked' ? BellOff : BellPlus
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={copy.label}
          title={copy.label}
          className={cn(HEADER_ICON_BUTTON, control.status === 'on' && 'text-primary', className)}>
          <Icon className="h-[17px] w-[17px]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[280px] p-3">
        <p className="m-0 text-sm font-semibold text-foreground">{copy.title}</p>
        <p className="m-0 mt-1 text-[13px] leading-snug text-muted-foreground">{copy.description}</p>
        {control.error ? <p className="m-0 mt-2 text-[13px] leading-snug text-destructive">{control.error}</p> : null}
        {copy.action ? (
          <DropdownMenuItem
            disabled={control.busy}
            onSelect={(event) => {
              // Keep the menu open so a refusal (the error line) stays visible.
              event.preventDefault()
              void (copy.action === 'enable' ? control.onEnable() : control.onDisable())
            }}
            className="mt-2.5 justify-center rounded-md border border-border py-2 text-sm font-medium">
            {copy.action === 'enable' ? 'Enable push on this device' : 'Turn off push on this device'}
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ── Push prompt ──────────────────────────────────────────────────────────────

/** How long "Not now" hides the push prompt before it asks again. */
export const PUSH_PROMPT_SNOOZE_MS = 24 * 60 * 60 * 1000

/**
 * Wait this long after load before asking. A device that already has push on
 * reads as `off` until its subscription has been checked, and must never see
 * the prompt flash.
 */
const PUSH_PROMPT_SETTLE_MS = 2500

/**
 * Whether to show the push prompt (PROD-1529). It keeps coming back until
 * push is on for this device: "Not now" only snoozes it. No control means the
 * device cannot do push here at all, so there is nothing to ask for.
 */
export function shouldShowPushPrompt({
  status,
  snoozedUntil,
  now,
}: {
  status: NotificationPushControl['status'] | undefined
  snoozedUntil: number | null
  now: number
}): boolean {
  if (!status || status === 'on') {
    return false
  }
  return snoozedUntil === null || now >= snoozedUntil
}

function readPushPromptSnooze(storageKey: string): number | null {
  try {
    const value = Number(window.localStorage.getItem(storageKey))
    return Number.isFinite(value) && value > 0 ? value : null
  } catch {
    return null
  }
}

function writePushPromptSnooze(storageKey: string, until: number): void {
  try {
    window.localStorage.setItem(storageKey, String(until))
  } catch {
    // Storage blocked (private mode): the prompt simply asks again next load.
  }
}

/** The prompt's wording for each state. Blocked and Home Screen give steps instead of re-asking the browser. */
const PUSH_PROMPT_COPY: Record<
  Exclude<NotificationPushControl['status'], 'on'>,
  { title: string; description: string; enable: boolean }
> = {
  off: {
    title: 'Turn on push notifications',
    description: PUSH_COPY.off.description,
    enable: true,
  },
  blocked: {
    title: PUSH_COPY.blocked.title,
    description:
      'Your browser is blocking notifications from this site. Allow them in the site settings (the icon left of the address, or iPhone Settings › Notifications), then reload.',
    enable: false,
  },
  'needs-home-screen': {
    title: PUSH_COPY['needs-home-screen'].title,
    description: PUSH_COPY['needs-home-screen'].description,
    enable: false,
  },
}

export interface NotificationPushPromptProps {
  /** The same control the bell uses: `pushControlFrom(usePushSubscription())`. */
  control?: NotificationPushControl
  /** Where "Not now" is remembered, one key per app (e.g. `tasks-push-prompt-snoozed-until`). */
  storageKey: string
  className?: string
}

/**
 * An in-app card asking this device to turn on push (PROD-1529). Mount it for
 * team members only. It stays until push is on: "Not now" hides it for a day,
 * then it returns. When the browser has blocked notifications, or an iPhone
 * needs the app on the Home Screen first, it explains the fix instead of
 * re-triggering a permission prompt the browser will refuse.
 */
export function NotificationPushPrompt({ control, storageKey, className }: NotificationPushPromptProps) {
  const [settled, setSettled] = React.useState(false)
  const [snoozedUntil, setSnoozedUntil] = React.useState<number | null>(null)

  React.useEffect(() => {
    setSnoozedUntil(readPushPromptSnooze(storageKey))
    const timer = setTimeout(() => setSettled(true), PUSH_PROMPT_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [storageKey])

  const status = control?.status
  if (!control || !settled || !shouldShowPushPrompt({ status, snoozedUntil, now: Date.now() })) {
    return null
  }
  const copy = PUSH_PROMPT_COPY[control.status as Exclude<NotificationPushControl['status'], 'on'>]

  const snooze = () => {
    const until = Date.now() + PUSH_PROMPT_SNOOZE_MS
    writePushPromptSnooze(storageKey, until)
    setSnoozedUntil(until)
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="notification-push-prompt-title"
      className={cn(
        'fixed inset-x-4 bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)] z-50 rounded-2xl border border-border bg-popover p-4 text-popover-foreground shadow-2xl',
        'md:inset-x-auto md:bottom-6 md:right-6 md:w-[360px]',
        className,
      )}>
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
          {control.status === 'blocked' ? <BellOff className="h-5 w-5" /> : <BellPlus className="h-5 w-5" />}
        </span>
        <div className="min-w-0 flex-1">
          <p id="notification-push-prompt-title" className="m-0 text-[15px] font-semibold">
            {copy.title}
          </p>
          <p className="m-0 mt-1 text-[13px] leading-snug text-muted-foreground">{copy.description}</p>
          {control.error ? <p className="m-0 mt-2 text-[13px] leading-snug text-destructive">{control.error}</p> : null}
        </div>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={snooze}
          className="h-10 rounded-lg px-3.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
          Not now
        </button>
        {copy.enable ? (
          <button
            type="button"
            disabled={control.busy}
            onClick={() => void control.onEnable()}
            className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50">
            {control.busy ? 'Enabling…' : 'Enable push'}
          </button>
        ) : null}
      </div>
    </div>
  )
}

function MarkAllButton({ inbox, enabled, className }: { inbox: Inbox; enabled: boolean; className?: string }) {
  if (!enabled) {
    return null
  }
  return (
    <button
      type="button"
      disabled={inbox.tabUnread === 0}
      onClick={inbox.markAllRead}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-40',
        className,
      )}>
      <CheckCheck className="h-[15px] w-[15px]" />
      {MARK_ALL_LABEL}
    </button>
  )
}

function ListSkeleton({ rows = 4, density }: { rows?: number; density: Density }) {
  const d = DENSITY[density]
  return (
    <div aria-busy="true">
      <div className={cn(d.section)}>
        <Skeleton className="h-3 w-16" />
      </div>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className={cn('flex items-start gap-3 pr-4', d.row)}>
          <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
          <span className="flex flex-1 flex-col gap-2 pt-0.5">
            <Skeleton className="h-3.5 w-[90%]" />
            <Skeleton className="h-3.5 w-[60%]" />
            <Skeleton className="h-3 w-16" />
          </span>
        </div>
      ))}
    </div>
  )
}

function CaughtUp({ description, compact = false }: { description: string; compact?: boolean }) {
  if (compact) {
    return <div className="px-4 py-12 text-center text-sm text-muted-foreground">All caught up</div>
  }
  return (
    <div className="flex flex-col items-center gap-2 px-5 py-16 text-center">
      <CircleCheck className="h-8 w-8 text-success-foreground" strokeWidth={1.8} />
      <span className="text-[15px] font-semibold text-foreground">You’re all caught up</span>
      <span className="text-[13px] text-muted-foreground">{description}</span>
    </div>
  )
}

function ListBody({
  props,
  inbox,
  handlers,
  compactEmpty = false,
}: {
  props: NotificationInboxProps
  inbox: Inbox
  handlers: ListHandlers
  compactEmpty?: boolean
}) {
  if (props.loading && props.items.length === 0) {
    return <ListSkeleton density={handlers.density} />
  }
  if (props.unavailable && props.items.length === 0) {
    return (
      <div role="status" className="px-5 py-12 text-center text-sm text-muted-foreground">
        {props.unavailableMessage ?? UNAVAILABLE}
      </div>
    )
  }
  if (inbox.sections.length === 0) {
    return <CaughtUp compact={compactEmpty} description={props.emptyDescription ?? DEFAULT_EMPTY} />
  }
  return <NotificationSections sections={inbox.sections} handlers={handlers} />
}

/**
 * The end of a list: the sentinel the next page loads from, a spinner while it
 * loads, "Show older" only when it will not load on its own (no observer, the
 * auto-load budget spent, or a failed page), and on the page a quiet end line.
 */
function LoadMoreFooter({
  props,
  loader,
  showEnd = false,
}: {
  props: NotificationInboxProps
  loader: UseLoadMoreResult
  showEnd?: boolean
}) {
  if (props.items.length === 0) {
    return null
  }
  const hasMore = Boolean(props.hasMore && props.onLoadMore)
  return (
    <div ref={loader.sentinelRef} data-notification-load-more="" className="px-3">
      {loader.loadingMore ? (
        <div
          role="status"
          className="flex items-center justify-center gap-2.5 py-[18px] text-[13px] text-muted-foreground">
          <span
            aria-hidden="true"
            className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-border border-t-primary"
          />
          Loading older notifications…
        </div>
      ) : hasMore ? (
        loader.autoLoading ? (
          <div aria-hidden="true" className="h-12" />
        ) : (
          <button
            type="button"
            onClick={() => void loader.loadMore()}
            className="my-2 h-10 w-full rounded-[10px] border border-border text-[13px] font-medium text-muted-foreground hover:bg-muted">
            Show older
          </button>
        )
      ) : showEnd ? (
        <div className="py-[18px] text-center text-[13px] text-muted-foreground">That’s everything.</div>
      ) : null}
    </div>
  )
}

// ── Bell ─────────────────────────────────────────────────────────────────────

/**
 * The bell. Desktop: a popover with All / Assigned / Mentioned / Following,
 * day sections, the Following group line, quick answers and "See all". Phones:
 * a full-screen sheet (grown out of the bell in the unified shell).
 */
export function NotificationCenter(props: NotificationCenterProps) {
  const { title = 'Notifications', triggerClassName, className, onSeeAll } = props
  const isMobile = useIsMobile()
  const shellMorph = isMobile && useShellLayout() === 'unified'
  const inbox = useInbox(props)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const listRef = React.useRef<HTMLDivElement | null>(null)
  const loader = useLoadMore({ hasMore: props.hasMore ?? false, onLoadMore: props.onLoadMore, resetKey: inbox.tab })
  const loaderRootRef = loader.rootRef
  const setListRef = React.useCallback(
    (element: HTMLDivElement | null) => {
      listRef.current = element
      loaderRootRef(element)
    },
    [loaderRootRef],
  )
  const [origin, setOrigin] = React.useState<CornerGrowOrigin | null>(null)
  const [open, setOpen] = React.useState(false)
  const [mounted, setMounted] = React.useState(false)
  const [visible, setVisible] = React.useState(false)
  const [expanded, setExpanded] = React.useState<ReadonlySet<string>>(() => new Set())
  const unreadLabel = notificationCenterUnreadLabel(inbox.counts.total)
  const unmountDelay = shellMorph ? MORPH_MS : 200

  React.useEffect(() => {
    if (!isMobile) {
      return
    }
    if (open) {
      setMounted(true)
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)))
      return
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), unmountDelay)
    return () => window.clearTimeout(timer)
  }, [open, unmountDelay, isMobile])

  useFollowingDayJump({
    inbox,
    rootRef: listRef,
    loader,
    canLoadMore: Boolean(props.hasMore && props.onLoadMore),
  })

  const handlers: ListHandlers = {
    density: isMobile ? 'phone' : 'popover',
    inbox: {
      ...inbox,
      open: (notification, target) => {
        inbox.open(notification, target)
        setOpen(false)
      },
    },
    expanded,
    onToggleExpanded: (id) =>
      setExpanded((current) => {
        const next = new Set(current)
        if (!next.delete(id)) {
          next.add(id)
        }
        return next
      }),
    onOpenFollowing: (group) => inbox.openFollowingAt(groupDayKey(group)),
  }

  const triggerClasses = cn(
    'relative inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none',
    triggerClassName,
  )
  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      aria-label={inbox.counts.total > 0 ? `${inbox.counts.total} unread notifications` : title}
      onClick={
        isMobile
          ? () => {
              const rect = triggerRef.current?.getBoundingClientRect()
              setOrigin(rect ? cornerGrowOrigin(rect, window.innerWidth) : null)
              setOpen(true)
            }
          : undefined
      }
      className={triggerClasses}>
      <Bell className="h-5 w-5" />
      {unreadLabel ? (
        <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-0.5 text-2xs font-bold text-destructive-foreground">
          {unreadLabel}
        </span>
      ) : null}
    </button>
  )

  if (isMobile) {
    const sheet = (
      <PhoneSheet
        props={props}
        inbox={inbox}
        handlers={handlers}
        listRef={setListRef}
        loader={loader}
        title={title}
        shell={shellMorph}
        onClose={() => setOpen(false)}
      />
    )
    if (shellMorph) {
      const o = origin ?? { top: 0, right: 0, width: 0, height: 0, radius: 0 }
      const box = visible
        ? { top: 0, right: 0, width: '100%', height: '100dvh', borderRadius: 0 }
        : { top: o.top, right: o.right, width: o.width, height: o.height, borderRadius: o.radius }
      return (
        <>
          {trigger}
          {mounted && typeof document !== 'undefined'
            ? createPortal(
                <>
                  <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={title}
                    // The outline only shapes the growing bell; once the panel
                    // fills the screen it would show as a white frame (PROD-1529).
                    className={cn(
                      'fixed z-modal overflow-hidden border bg-background motion-reduce:transition-none',
                      visible ? 'border-transparent' : 'border-border',
                    )}
                    style={{
                      ...box,
                      transition: ['top', 'right', 'width', 'height', 'border-radius', 'border-color']
                        .map((prop) => `${prop} ${MORPH_MS}ms ${MORPH_EASE}`)
                        .join(', '),
                    }}>
                    {/* Full-size from the first frame and pinned top-right, so
                        the content is revealed by the growing box, not reflowed. */}
                    <div
                      className={cn(
                        'absolute right-0 top-0 h-dvh w-screen transition-opacity duration-150',
                        visible ? 'opacity-100 delay-100' : 'opacity-0',
                      )}>
                      {sheet}
                    </div>
                  </div>
                  {/* The bell becomes the close button, in the bell's own spot. */}
                  <button
                    type="button"
                    aria-label="Close notifications"
                    onClick={() => setOpen(false)}
                    className={cn(triggerClasses, 'fixed z-[calc(var(--z-index-modal)+1)]')}
                    style={{ top: o.top, right: o.right }}>
                    <Bell
                      className={cn(
                        'absolute h-5 w-5 transition-[transform,opacity] duration-300',
                        visible ? 'rotate-90 scale-50 opacity-0' : 'opacity-100',
                      )}
                    />
                    <X
                      className={cn(
                        'absolute h-5 w-5 transition-[transform,opacity] duration-300',
                        visible ? 'opacity-100' : '-rotate-90 scale-50 opacity-0',
                      )}
                    />
                  </button>
                </>,
                document.body,
              )
            : null}
        </>
      )
    }
    return (
      <>
        {trigger}
        {mounted && typeof document !== 'undefined'
          ? createPortal(
              <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className={cn(
                  'fixed inset-0 z-modal bg-background transition-transform duration-200 ease-out',
                  visible ? 'translate-x-0' : 'translate-x-full',
                )}>
                {sheet}
              </div>,
              document.body,
            )
          : null}
      </>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        aria-label={title}
        className={cn(
          'flex h-[min(640px,var(--radix-popover-content-available-height))] w-[min(440px,calc(100vw-16px))] flex-col overflow-hidden rounded-[14px] bg-popover p-0 shadow-2xl [--notification-surface:var(--color-popover)]',
          className,
        )}>
        <div className="flex items-center justify-between gap-2 px-4 pb-1 pt-3.5">
          <span className="text-base font-semibold">{title}</span>
          <span className="flex items-center gap-1">
            {props.headerActions}
            <SettingsLink href={props.settingsHref} className="h-8 w-8" />
            <PushControlMenu control={props.pushControl} className="h-8 w-8" />
            <MarkAllButton inbox={inbox} enabled={Boolean(props.onMarkAllRead)} className="h-8 text-[13px]" />
          </span>
        </div>
        <TabPills inbox={inbox} size="sm" className="border-b border-border px-4 pb-3 pt-1.5" />
        <div ref={setListRef} data-list="notifications" className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <ListBody props={props} inbox={inbox} handlers={handlers} compactEmpty />
          <LoadMoreFooter props={props} loader={loader} />
        </div>
        {onSeeAll ? (
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              onSeeAll()
            }}
            className="flex h-[50px] shrink-0 items-center justify-center border-t border-border text-sm font-medium text-foreground/90 transition-colors hover:bg-muted/60">
            See all notifications
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}

function PhoneSheet({
  props,
  inbox,
  handlers,
  listRef,
  loader,
  title,
  shell,
  onClose,
}: {
  props: NotificationInboxProps
  inbox: Inbox
  handlers: ListHandlers
  listRef: React.Ref<HTMLDivElement>
  loader: UseLoadMoreResult
  title: string
  /** Unified shell: the bell-turned-X closes, so the header leaves room for it. */
  shell: boolean
  onClose: () => void
}) {
  return (
    <div className="relative flex h-full flex-col bg-background [--notification-surface:var(--color-background)]">
      <div
        className={cn(
          'flex shrink-0 items-center gap-2 pl-[18px]',
          shell
            ? 'mt-[calc(env(safe-area-inset-top,0px)+0.75rem)] h-14 pr-20'
            : 'pr-3.5 pt-[calc(env(safe-area-inset-top,0px)+0.875rem)] pb-1',
        )}>
        <h1 className="m-0 flex-1 text-[26px] font-bold leading-tight">{title}</h1>
        {props.headerActions}
        <SettingsLink href={props.settingsHref} className="h-11 w-11 rounded-full [&>svg]:h-[22px] [&>svg]:w-[22px]" />
        <PushControlMenu
          control={props.pushControl}
          className="h-11 w-11 rounded-full [&>svg]:h-[22px] [&>svg]:w-[22px]"
        />
        {shell ? null : (
          <button
            type="button"
            aria-label="Close notifications"
            onClick={onClose}
            className="grid h-[52px] w-[52px] place-items-center rounded-full border border-border text-foreground">
            <X className="h-[22px] w-[22px]" />
          </button>
        )}
      </div>
      <TabPills
        inbox={inbox}
        size="lg"
        className="shrink-0 gap-1 overflow-x-auto border-b border-border px-4 pb-3.5 pt-2"
      />
      <div ref={listRef} data-list="notifications" className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-24">
        <ListBody props={props} inbox={inbox} handlers={handlers} compactEmpty />
        <LoadMoreFooter props={props} loader={loader} />
      </div>
      {props.onMarkAllRead && inbox.tabUnread > 0 ? (
        // Where the thumb is, and only while something in this tab is unread.
        <button
          type="button"
          onClick={inbox.markAllRead}
          className="absolute bottom-[calc(env(safe-area-inset-bottom,0px)+1.75rem)] left-1/2 flex h-12 -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full border border-border bg-background/90 px-[22px] text-[15px] font-medium text-foreground shadow-lg backdrop-blur-xl transition-colors hover:bg-muted active:scale-95">
          <CheckCheck className="h-[18px] w-[18px]" />
          {MARK_ALL_LABEL}
        </button>
      ) : null}
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

/** True when a key press belongs to something else on the page (typing, a menu, a dialog). */
function keyBelongsElsewhere(event: KeyboardEvent): boolean {
  if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey || event.defaultPrevented) {
    return true
  }
  const target = event.target as HTMLElement | null
  if (!target || typeof target.closest !== 'function') {
    return false
  }
  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable="true"], [role="menu"], [role="dialog"], [role="listbox"]',
    ),
  )
}

/** The id ↑/↓ moves to, or `'more'` when ↓ runs off the end. Exported for tests. */
export function nextNotificationSelection(
  order: readonly string[],
  current: string | null,
  key: 'ArrowUp' | 'ArrowDown',
): string | 'more' | null {
  const index = current ? order.indexOf(current) : -1
  if (key === 'ArrowUp') {
    return index > 0 ? (order[index - 1] ?? null) : null
  }
  if (index === -1) {
    return order[0] ?? null
  }
  return index < order.length - 1 ? (order[index + 1] ?? null) : 'more'
}

/**
 * /notifications. Desktop: rows on the left, the selected notification's right
 * side on the right; ↑/↓ move, Enter opens, and older pages load at the
 * bottom. Phones: the list alone, and a tap opens.
 */
export function NotificationCenterPage(props: NotificationCenterPageProps) {
  const { title = 'Notifications', className, loadDetail, hasMore = false } = props
  const isMobile = useIsMobile()
  const inbox = useInbox(props)
  const [selectedId, setSelectedId] = React.useState<string | null>(props.defaultSelectedId ?? null)
  const [detail, setDetail] = React.useState<{ id: string; state: NotificationDetailState | null } | null>(null)
  const [retry, setRetry] = React.useState(0)
  const selectAfterLoad = React.useRef<string | null>(null)
  const loader = useLoadMore({ hasMore, onLoadMore: props.onLoadMore, resetKey: inbox.tab })
  const { loadMore } = loader
  // The list's scroll box on desktop; on phones the whole page scrolls, so the jump searches the page.
  const listRef = React.useRef<HTMLElement | null>(null)
  const loaderRootRef = loader.rootRef
  const setListRef = React.useCallback(
    (element: HTMLElement | null) => {
      listRef.current = element
      loaderRootRef(element)
    },
    [loaderRootRef],
  )
  useFollowingDayJump({ inbox, rootRef: listRef, loader, canLoadMore: Boolean(hasMore && props.onLoadMore) })

  const order = React.useMemo(() => notificationEntryOrder(inbox.sections), [inbox.sections])
  const selected = findNotificationEntry(inbox.sections, selectedId)
  const selectedNotification = selected?.kind === 'row' ? selected.notification : null

  const select = React.useCallback(
    (entry: InboxEntry | null) => {
      setSelectedId(entry?.id ?? null)
      if (entry && entry.kind !== 'group') {
        inbox.setRead(entryNotifications(entry), true)
      }
      if (entry && typeof document !== 'undefined') {
        window.setTimeout(() => {
          document
            .querySelector(`[data-notification-entry="${CSS.escape(entry.id)}"]`)
            ?.scrollIntoView({ block: 'nearest' })
        }, 0)
      }
    },
    [inbox],
  )

  const openFollowing = (group: InboxGroupEntry, entry?: InboxItemEntry) => {
    inbox.openFollowingAt(groupDayKey(group))
    const target = entry ?? group.entries[0]
    setSelectedId(target?.id ?? null)
    if (target) {
      inbox.setRead(entryNotifications(target), true)
    }
  }

  // ↓ past the last row loaded the next page; select its first row once it lands.
  React.useEffect(() => {
    const after = selectAfterLoad.current
    if (!after) {
      return
    }
    const next = nextNotificationSelection(
      order.map((entry) => entry.id),
      after,
      'ArrowDown',
    )
    if (next && next !== 'more') {
      selectAfterLoad.current = null
      select(order.find((entry) => entry.id === next) ?? null)
    }
  }, [order, select])

  // The right side is fetched live from the owning app each time a row is picked.
  React.useEffect(() => {
    if (!selectedNotification || !loadDetail) {
      setDetail(null)
      return
    }
    let cancelled = false
    const id = selectedNotification.id
    setDetail({ id, state: { status: 'loading' } })
    loadDetail(selectedNotification)
      .then((state) => {
        if (!cancelled) {
          setDetail({ id, state })
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDetail({ id, state: { status: 'unavailable' } })
        }
      })
    return () => {
      cancelled = true
    }
    // Re-fetch on a new selection or Retry, not on every list refetch.
  }, [selectedNotification?.id, loadDetail, retry])

  // Keyboard: ↑/↓ move through the rows you can see (rows folded into a group line are skipped); Enter opens.
  React.useEffect(() => {
    if (isMobile) {
      return
    }
    const onKey = (event: KeyboardEvent) => {
      if (keyBelongsElsewhere(event) || !['ArrowUp', 'ArrowDown', 'Enter'].includes(event.key)) {
        return
      }
      if (event.key === 'Enter') {
        if (!selected) {
          return
        }
        event.preventDefault()
        if (selected.kind === 'group') {
          openFollowing(selected)
        } else {
          inbox.open(entryNotifications(selected)[0] as InboxNotification)
        }
        return
      }
      event.preventDefault()
      const ids = order.map((entry) => entry.id)
      const next = nextNotificationSelection(ids, selectedId, event.key as 'ArrowUp' | 'ArrowDown')
      if (next === 'more') {
        if (hasMore) {
          selectAfterLoad.current = selectedId
          void loadMore()
        }
        return
      }
      if (next) {
        select(order.find((entry) => entry.id === next) ?? null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const handlers: ListHandlers = {
    density: isMobile ? 'phone' : 'page',
    inbox,
    selectedId: isMobile ? null : selectedId,
    onSelect: isMobile ? undefined : select,
    onOpenFollowing: openFollowing,
    expanded: isMobile ? undefined : new Set(),
  }
  const [phoneExpanded, setPhoneExpanded] = React.useState<ReadonlySet<string>>(() => new Set())
  if (isMobile) {
    handlers.expanded = phoneExpanded
    handlers.onToggleExpanded = (id) =>
      setPhoneExpanded((current) => {
        const next = new Set(current)
        if (!next.delete(id)) {
          next.add(id)
        }
        return next
      })
  }

  const showInitialSkeleton = props.loading && props.items.length === 0
  const list = (
    <>
      <ListBody props={props} inbox={inbox} handlers={handlers} />
      <LoadMoreFooter props={props} loader={loader} showEnd={inbox.sections.length > 0} />
    </>
  )

  const header = (
    <div className="shrink-0 bg-background px-5 pb-3 pt-5">
      <div className="flex items-center justify-between gap-2">
        <h1 className="m-0 text-[22px] font-semibold leading-7">{title}</h1>
        <span className="flex items-center gap-1">
          {props.headerActions}
          <SettingsLink href={props.settingsHref} className="h-9 w-9" />
          <PushControlMenu control={props.pushControl} className="h-9 w-9" />
          <MarkAllButton inbox={inbox} enabled={Boolean(props.onMarkAllRead)} className="h-9 text-sm" />
        </span>
      </div>
      <TabPills inbox={inbox} size={isMobile ? 'lg' : 'md'} className="overflow-x-auto pt-3" />
    </div>
  )

  if (isMobile) {
    return (
      <div
        className={cn(
          'flex min-h-full flex-col bg-background [--notification-surface:var(--color-background)]',
          className,
        )}>
        {header}
        <div className="border-t border-border pb-16">{list}</div>
      </div>
    )
  }

  const detailState =
    detail && detail.id === selectedNotification?.id
      ? detail.state
      : loadDetail && selectedNotification
        ? { status: 'loading' as const }
        : null

  return (
    // The page fills its host's height (capped at the viewport when the host
    // gives it none) and each column scrolls on its own, so the list loads
    // older pages as its own bottom comes into view.
    <div
      className={cn(
        'flex h-full max-h-dvh min-h-0 w-full bg-background [--notification-surface:var(--color-background)]',
        className,
      )}>
      <section
        aria-label={title}
        className="flex h-full min-h-0 w-[min(480px,45%)] shrink-0 flex-col border-r border-border">
        {header}
        <div
          ref={setListRef}
          data-list="notifications"
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-4">
          {list}
        </div>
      </section>
      <section aria-label="Selected notification" className="h-full min-w-0 flex-1 overflow-y-auto">
        {showInitialSkeleton || selected ? (
          // The detail is a column of its own: a full-height rule on its right
          // edge marks where it ends, matching the list's divider.
          <div className="min-h-full max-w-[640px] border-r border-border">
            {showInitialSkeleton ? (
              <NotificationDetailSkeletonPane />
            ) : selected?.kind === 'group' ? (
              <NotificationGroupDetail
                group={selected}
                now={inbox.now}
                isUnread={(entry) => entryIsUnread(entry, inbox.isRead)}
                onOpenFollowing={(entry) => openFollowing(selected, entry)}
                onToggleRead={(read) => inbox.setRead(entryNotifications(selected), read)}
              />
            ) : selected ? (
              <NotificationDetail
                key={selected.id}
                entry={selected}
                state={selected.kind === 'row' ? detailState : null}
                unread={entryIsUnread(selected, inbox.isRead)}
                answer={selected.kind === 'row' ? inbox.answers[selected.notification.id] : undefined}
                unfollowed={inbox.isUnfollowed(entryNotifications(selected)[0] as InboxNotification)}
                now={inbox.now}
                onOpen={(notification, target) => inbox.open(notification, target)}
                onToggleRead={() => inbox.setRead(entryNotifications(selected), !entryIsUnread(selected, inbox.isRead))}
                onUnfollow={
                  inbox.unfollow
                    ? () => inbox.unfollow?.(entryNotifications(selected)[0] as InboxNotification)
                    : undefined
                }
                onFollowAgain={
                  inbox.followAgain
                    ? () => inbox.followAgain?.(entryNotifications(selected)[0] as InboxNotification)
                    : undefined
                }
                onAnswer={
                  inbox.answer && selected.kind === 'row'
                    ? (action) => void inbox.answer?.(selected.notification, action)
                    : undefined
                }
                onReply={props.onReply}
                onRetry={() => setRetry((count) => count + 1)}
              />
            ) : null}
          </div>
        ) : (
          <NotificationDetailEmpty caughtUp={!props.loading && inbox.sections.length === 0} />
        )}
      </section>
    </div>
  )
}

function NotificationDetailSkeletonPane() {
  return (
    <div aria-busy="true" className="flex max-w-[640px] flex-col gap-6 px-10 pb-12 pt-8">
      <div className="flex items-center gap-4">
        <Skeleton className="h-14 w-14 shrink-0 rounded-full" />
        <span className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-[18px] w-[85%]" />
          <Skeleton className="h-3 w-[30%]" />
        </span>
      </div>
      <Skeleton className="h-24 w-full rounded-xl" />
      <Skeleton className="h-[42px] w-[170px] rounded-[10px]" />
    </div>
  )
}
